/**
 * Der Ring der Verwendungs-Karte — aus `ui_kits/dashboard/charts.jsx` (HM-076).
 *
 * **Kein Diagramm im Sinne von ADR 0021**, sondern ein Bild aus zwei Bögen:
 * Wohin ging die Erzeugung — ins Haus oder ins Netz. Die Farben sind die der
 * Größenachse (ADR 0013), und in der Mitte steht die eine Zahl, um die es geht.
 * Ohne Werte (beide null) bleibt der Ring grau statt eine Aufteilung zu
 * behaupten, die niemand gemessen hat.
 */
export interface DonutSegment {
  readonly value: number;
  readonly color: string;
  readonly label: string;
}

export function Donut({
  segments,
  size = 150,
  thickness = 22,
  centerLabel,
  centerSub,
}: {
  readonly segments: readonly DonutSegment[];
  readonly size?: number;
  readonly thickness?: number;
  readonly centerLabel: string;
  readonly centerSub?: string;
}) {
  const total = segments.reduce((sum, segment) => sum + segment.value, 0);
  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  return (
    <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }} role="img" aria-label={segments.map((s) => `${s.label} ${s.value}`).join(", ")}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="var(--surface-sunken)" strokeWidth={thickness} />
        {total > 0 &&
          segments.map((segment) => {
            const dash = (circumference * segment.value) / total;
            const element = (
              <circle
                key={segment.label}
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke={segment.color}
                strokeWidth={thickness}
                strokeDasharray={`${dash} ${circumference - dash}`}
                strokeDashoffset={-offset}
              />
            );
            offset += dash;
            return element;
          })}
      </svg>
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <span style={{ fontFamily: "var(--font-mono)", fontSize: 22, fontWeight: 600, color: "var(--text-strong)" }}>
          {centerLabel}
        </span>
        {centerSub !== undefined && <span style={{ fontSize: 11, color: "var(--text-muted)" }}>{centerSub}</span>}
      </div>
    </div>
  );
}
